import { createContext, useContext, useEffect, useState } from "react";
import { UserPreferences } from "@/types";
import { supabase } from "@/integrations/supabase/client";

interface PreferencesContextType {
  preferences: UserPreferences | null;
  loading: boolean;
  refreshPreferences: () => Promise<void>;
}

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error("usePreferences must be used within a PreferencesProvider");
  }
  return context;
};

export const PreferencesProvider = ({ children }: { children: React.ReactNode }) => {
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchPreferences = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setPreferences(null);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from("user_preferences")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error && error.code !== "PGRST116") {
        console.error("Error fetching preferences:", error);
        setLoading(false);
        return;
      }

      if (!data) {
        // Create default preferences
        const defaultPrefs = {
          user_id: user.id,
          theme: "system" as const,
          number_format: "comma" as const,
          date_format: "DD/MM/YYYY" as const,
          auto_logout_minutes: 0,
          default_transaction_type: "cash-out" as const,
          default_payment_method: "cash" as const,
          stay_logged_in: false,
          app_mode: "cashbook" as const,
          has_selected_initial_mode: false,
          password_prompt_dismissed: false,
        };

        const { data: newData, error: insertError } = await supabase
          .from("user_preferences")
          .insert(defaultPrefs)
          .select()
          .single();

        if (insertError) {
          console.error("Error creating preferences:", insertError);
          setLoading(false);
          return;
        }

        setPreferences({
          ...newData,
          theme: newData.theme as "light" | "dark" | "system",
          number_format: newData.number_format as "comma" | "dot" | "space",
          date_format: newData.date_format as "DD/MM/YYYY" | "MM/DD/YYYY" | "YYYY-MM-DD",
          default_transaction_type: newData.default_transaction_type as "cash-in" | "cash-out",
          default_payment_method: newData.default_payment_method as "cash" | "online",
          stay_logged_in: newData.stay_logged_in ?? false,
          app_mode: (newData.app_mode as "cashbook" | "ims") ?? "cashbook",
          has_selected_initial_mode: newData.has_selected_initial_mode ?? false,
          password_prompt_dismissed: newData.password_prompt_dismissed ?? false,
          posting_changes: (newData.posting_changes as "check_and_post" | "direct_to_ledger") ?? "direct_to_ledger",
          created_at: new Date(newData.created_at),
          updated_at: new Date(newData.updated_at),
        });
      } else {
        setPreferences({
          ...data,
          theme: data.theme as "light" | "dark" | "system",
          number_format: data.number_format as "comma" | "dot" | "space",
          date_format: data.date_format as "DD/MM/YYYY" | "MM/DD/YYYY" | "YYYY-MM-DD",
          default_transaction_type: data.default_transaction_type as "cash-in" | "cash-out",
          default_payment_method: data.default_payment_method as "cash" | "online",
          stay_logged_in: data.stay_logged_in ?? false,
          app_mode: (data.app_mode as "cashbook" | "ims") ?? "cashbook",
          has_selected_initial_mode: data.has_selected_initial_mode ?? false,
          password_prompt_dismissed: data.password_prompt_dismissed ?? false,
          posting_changes: (data.posting_changes as "check_and_post" | "direct_to_ledger") ?? "direct_to_ledger",
          created_at: new Date(data.created_at),
          updated_at: new Date(data.updated_at),
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const refreshPreferences = async () => {
    setLoading(true);
    await fetchPreferences();
  };

  useEffect(() => {
    fetchPreferences();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        fetchPreferences();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <PreferencesContext.Provider value={{ preferences, loading, refreshPreferences }}>
      {children}
    </PreferencesContext.Provider>
  );
};